import { create } from 'zustand';
import { supabase } from '@/db/supabase';
import { isAuthRetryableFetchError, type User, type Session } from '@supabase/supabase-js';
import type { PublicUser, Org } from '@/types/db';
import { identify, resetIdentity, reportEvent } from '@/shared/services/telemetry';
import { gstinsOf } from '@/shared/org/orgGstins';
import { npoNumbersOf } from '@/shared/org/npoNumbers';
import { signerDinOf } from '@/shared/org/signerDin';
import { dbErrorMessage, isDatabaseWaking, isNetworkFailure } from '@/shared/utils/edgeError';
import { isSessionExpired, noteSessionExpired } from '@/shared/auth/linkError';
import { rememberAuthEmail, forgetAuthEmail } from '@/shared/auth/lastEmail';
import { forgetDayBook } from '@/shared/office/dayBookCache';
import { forgetPages } from '@/shared/office/pageCache';
import { forgetRecentPlaces } from '@/shared/search/recentPlaces';
import { forgetAskAiMemory } from '@/shared/search/askAiMemory';
import { isSideView } from '@/shared/sideview/frame';
import { keepSignInStorage } from '@/shared/pwa/installed';
import { forgetPushOnSignOut } from '@/shared/push/webPush';

interface AuthState {
    session: Session | null;
    user: User | null; // The Auth User (Supabase Auth)
    profile: PublicUser | null; // The DB User (public.users)
    orgs: Org[];
    /**
     * Why `orgs` is empty, when the reason is NOT "they have none".
     *
     * CAREFUL. An empty `orgs` used to mean one thing to every reader: brand-new
     * account, send them to /onboarding. It actually means two things, and the
     * second one is a failure — an expired token, an RLS change, a network
     * blip, a `public.users` row we could not read — after which a customer of
     * two years lands on "Tell us about your business" and is asked to create
     * the org they already have. Reported live 11 Aug 2026.
     *
     * `null` = we read the memberships and there really are none. A string =
     * we could not read them, and NOBODY may treat that as zero. Same shape of
     * mistake as `packet-missing-figure-is-not-a-zero`.
     */
    orgsUnreadable: string | null;
    currentOrg: Org | null;

    // Practice Management — "Enter Client" flow
    viewingAsClient: boolean;
    originalPracticeOrg: Org | null;
    viewingClientName: string | null;

    loading: boolean;
    initialized: boolean;

    // Actions
    initialize: () => Promise<void>;
    signInWithGoogle: () => Promise<{ error: Error | null }>;
    signInWithEmail: (email: string) => Promise<{ error: Error | null }>;
    /**
     * Sign in with the 6-digit code from the same email as the link.
     *
     * The link and the code are the SAME one-time token, so whichever arrives
     * first wins and a scanner that spent the link has spent the code too.
     * The point is that a scanner cannot spend a code it never clicks.
     */
    signInWithEmailCode: (email: string, code: string) => Promise<{ error: Error | null }>;
    signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>;
    /** Set (or change) this account's password; also flips the `has_password` flag. */
    setPassword: (password: string) => Promise<{ error: Error | null }>;
    /** Remember that they declined the set-password offer, so we ask once only. */
    dismissPasswordPrompt: () => Promise<void>;
    sendPasswordReset: (email: string) => Promise<{ error: Error | null }>;
    signOut: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    /**
     * Was this failure a dead sign-in? First try to bring it back with the
     * refresh token; only when that is refused, end the session and say so.
     *
     * @param mayRevive - false once a revive was already tried for this read
     * @returns 'revived' when a fresh token was obtained and the caller should
     *   read again, true when it ended the session and the caller must stop.
     */
    endDeadSession: (error: unknown, at: string, mayRevive?: boolean) => Promise<boolean | 'revived'>;
    switchOrg: (orgId: string) => void;
    /** Patch an org's cached fields (e.g. after Settings save) so currentOrg stays in sync without a reload. */
    updateOrgLocal: (orgId: string, patch: Partial<Org>) => void;
    enterClientOrg: (clientOrgId: string, clientName: string) => void;
    exitClientOrg: () => void;
}

/** Persist the active org id so a refresh keeps the user on the entity they were
 *  working in (e.g. after diving into a client from the Portfolio Cockpit) rather
 *  than snapping back to orgs[0]. */
const ACTIVE_ORG_KEY = 'karr_active_org';
// "Viewing client" context must survive a page reload — the banner and Exit
// button read it, while currentOrg alone can't tell a client visit from a
// normal org switch. Stored alongside the active org id.
const VIEWING_CLIENT_KEY = 'karr_viewing_client';
// sideview: the copy of Karr in the side view's panel follows the main window's
// org and never leads it. A page in the panel that switches org switches the
// PANEL; written to storage, it would also move the main window on its next
// reload, into books nobody chose there.
const IN_SIDE_VIEW = isSideView();
function rememberViewingClient(ctx: { practiceOrgId: string; clientName: string } | null) {
    if (IN_SIDE_VIEW) return;
    try {
        if (ctx) localStorage.setItem(VIEWING_CLIENT_KEY, JSON.stringify(ctx));
        else localStorage.removeItem(VIEWING_CLIENT_KEY);
    } catch { /* ignore */ }
}
function recallViewingClient(): { practiceOrgId: string; clientName: string } | null {
    try {
        const raw = localStorage.getItem(VIEWING_CLIENT_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}
function rememberActiveOrg(orgId: string | null) {
    if (IN_SIDE_VIEW) return;
    try {
        if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId);
        else localStorage.removeItem(ACTIVE_ORG_KEY);
    } catch { /* ignore (private mode / disabled storage) */ }
}
function recallActiveOrg(): string | null {
    try { return localStorage.getItem(ACTIVE_ORG_KEY); } catch { return null; }
}

/**
 * The waits between goes at a read that died at the NETWORK layer (the fetch
 * never got an HTTP answer). Two more goes, under two and a half seconds in
 * all: enough to ride out a radio waking up or wifi changing hands, short
 * enough that a genuinely dead connection still reaches the OrgsUnavailable
 * panel fast — whose 'online' listener then owns the retrying.
 */
const NETWORK_RETRY_WAITS_MS = [600, 1800];
async function networkRetryWait(attempt: number): Promise<boolean> {
    if (attempt >= NETWORK_RETRY_WAITS_MS.length) return false;
    // The browser KNOWS it is offline: waiting here would only delay the panel
    // that can actually see the connection come back.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
    await new Promise((resolve) => setTimeout(resolve, NETWORK_RETRY_WAITS_MS[attempt]));
    return true;
}

/**
 * The waits while the DATABASE is out of reach (PostgREST answers PGRST002
 * "Could not query the database for the schema cache. Retrying." during a
 * restart). The server is saying it will be back; about fifteen seconds
 * behind the loading screen covers a restart, where a full-page error with a
 * "Try again" button only made the person do the waiting by hand
 * (schemawait, founder, 30 Oct 2026). Past that, the panel keeps trying.
 */
const DB_WAKING_WAITS_MS = [1000, 2000, 3000, 4000, 5000];

/** One more go at the boot read, after a wait, when the failure fixes itself. */
async function transientRetryWait(error: unknown, attempt: number): Promise<boolean> {
    if (isNetworkFailure(error)) return networkRetryWait(attempt);
    if (!isDatabaseWaking(error) || attempt >= DB_WAKING_WAITS_MS.length) return false;
    await new Promise((resolve) => setTimeout(resolve, DB_WAKING_WAITS_MS[attempt]));
    return true;
}

export const useAuth = create<AuthState>((set, get) => ({
    session: null,
    user: null,
    profile: null,
    orgs: [],
    orgsUnreadable: null,
    currentOrg: null,
    viewingAsClient: false,
    originalPracticeOrg: null,
    viewingClientName: null,
    loading: true,
    initialized: false,

    initialize: async () => {
        if (get().initialized) return;

        try {
            // Get initial session
            const { data: { session } } = await supabase.auth.getSession();

            if (!session) {
                set({ session: null, user: null, profile: null, orgs: [], orgsUnreadable: null, loading: false, initialized: true });
            } else {
                // Set session immediately
                set({ session, user: session.user });
                keepSignInStorage();
                // Fetch profile and orgs
                await get().refreshProfile();
            }

            // Listen for changes
            supabase.auth.onAuthStateChange(async (_event, session) => {
                const prevUserId = get().session?.user?.id;
                const nextUserId = session?.user?.id;
                // Always keep the latest session/token so a TOKEN_REFRESHED (or
                // USER_UPDATED) event for the SAME user doesn't leave a stale,
                // about-to-expire access token in the store.
                set({ session, user: session?.user ?? null });
                if (session) keepSignInStorage();
                // Only the heavier profile/org reload is gated on the user changing.
                if (nextUserId !== prevUserId) {
                    if (session) {
                        set({ loading: true });
                        await get().refreshProfile();
                    } else {
                        set({ profile: null, orgs: [], orgsUnreadable: null, currentOrg: null, loading: false });
                    }
                }
            });
        } catch (error) {
            console.error('Auth initialization failed:', error);
            // Whatever threw, `orgs` is still the initial [] and we never got to
            // ask. That is unreadable, not zero — see `orgsUnreadable`.
            reportEvent('orgs_unreadable', { at: 'initialize', code: isNetworkFailure(error) ? 'network' : 'threw' });
            set({ loading: false, initialized: true, orgsUnreadable: dbErrorMessage(error) });
        }
    },

    endDeadSession: async (error: unknown, at: string, mayRevive = true) => {
        if (!isSessionExpired(error)) return false;
        // staylogged: an expired ACCESS token is not a dead sign-in. It lasts an
        // hour, and a phone that slept with Karr in the background wakes holding
        // a stale one before the refresh timer has run. Signing out on that
        // 401 is what logged phones out "every time I come back" (3 Oct 2026).
        // The refresh token is the real sign-in: ask it first.
        if (mayRevive) {
            try {
                const { data, error: refreshError } = await supabase.auth.refreshSession();
                if (data.session && !refreshError) {
                    reportEvent('session_revived', { at });
                    set({ session: data.session, user: data.session.user });
                    return 'revived';
                }
                // A refresh that never reached the server says nothing about the
                // sign-in. Keep it; the retry panel can try again once online.
                if (refreshError && (isAuthRetryableFetchError(refreshError) || isNetworkFailure(refreshError))) return false;
            } catch (refreshThrew) {
                if (isNetworkFailure(refreshThrew)) return false;
            }
        }
        // The single likeliest reason a membership read fails, and the one case
        // where the "We couldn't load your businesses / Try again" panel would
        // lie: retrying a dead JWT fails forever. So don't offer a button that
        // cannot work. End the session, leave the sentence the sign-in screen
        // will show, and let the ordinary `!session` guards do the rest.
        reportEvent('session_expired_midflight', { at });
        noteSessionExpired();
        set({ loading: false, initialized: true, orgsUnreadable: null });
        await get().signOut();
        return true;
    },

    refreshProfile: async () => {
        let { user } = get();
        // A boot that failed before a session was ever read (initialize()'s
        // getSession threw on a dead network) leaves `user` null — and the
        // OrgsUnavailable panel's Try again calls THIS function, so it must be
        // able to pick that boot up rather than silently doing nothing behind
        // a button that then looks broken.
        if (!user) {
            try {
                const { data } = await supabase.auth.getSession();
                if (!data.session) return;
                set({ session: data.session, user: data.session.user });
                user = data.session.user;
            } catch {
                return; // still unreachable — the panel stays, its next retry asks again
            }
        }

        // NETWORK BLIPS RETRY THEMSELVES. Karr is an installed PWA: on a wobbly
        // connection the precached shell paints perfectly, and THIS read is the
        // first thing to touch the network — so a one-second blip at open used
        // to reach clients as a full-screen "We couldn't load your businesses"
        // ("over and over", 28 Sep 2026). A network-layer death (isNetworkFailure:
        // the fetch never got an HTTP answer) gets two more goes before the
        // panel does. Real API answers — 401, RLS, a missing column — are never
        // retried: they fail identically forever.
        // One go at reviving an expired token per read (see endDeadSession).
        let revived = false;
        for (let attempt = 0; ; attempt++) {
            try {
                // 1. Fetch Public Profile
                const { data: profile, error: profileError } = await supabase
                    .from('users')
                    .select('*')
                    .eq('auth_id', user.id)
                    .single();

                // PGRST116 is "no row" — a real answer, and the normal one for a
                // signup whose `public.users` trigger has not landed yet. Anything
                // else is a failure to read, and must not end up looking like a
                // fresh account.
                const profileMissing = !!profileError && profileError.code === 'PGRST116';
                if (profileError && !profileMissing) {
                    if (await transientRetryWait(profileError, attempt)) continue;
                    const dead = await get().endDeadSession(profileError, 'profile', !revived);
                    if (dead === 'revived') { revived = true; continue; }
                    if (dead) return;
                    console.error('Error fetching profile:', profileError);
                    reportEvent('orgs_unreadable', { at: 'profile', code: isNetworkFailure(profileError) ? 'network' : (profileError.code ?? 'none') });
                    set({ loading: false, initialized: true, orgsUnreadable: dbErrorMessage(profileError) });
                    return;
                }

                // 2. Fetch Orgs via Memberships
                //
                // `org_members.user_id` is the `public.users` id, not `auth.uid()` —
                // with no profile row there is no id to key on, and the query the old
                // code still sent asked PostgREST for `user_id=eq.undefined`, which
                // Postgres rejects as an invalid uuid. That failure then read as "no
                // orgs". No profile row is the brand-new / trigger-lag case, so answer
                // it here: genuinely nothing yet, and nothing broken.
                if (!profile) {
                    set({ profile: null, orgs: [], orgsUnreadable: null, currentOrg: null, loading: false, initialized: true });
                    return;
                }

                // ⚠⚠ `is_practice` (ob-5) is asked for SEPARATELY-TOLERANTLY on purpose.
                // Merging a PR does not change the live database — the founder applies
                // migrations by hand — so this client ships BEFORE the column exists.
                // PostgREST rejects the whole query when a select names a column that
                // is not there (the exact shape of ob-3's `total_amount` bug), and this
                // particular query decides `orgs`. Asking for it unconditionally would
                // have locked EVERY user out of Karr for the window between merge and
                // apply.
                // fystart: fy_start_month (00001) so every "FY start" / "FY end" button
                // answers with the business's own year, not its country's, at no extra call.
                const ORG_COLS = 'id, name, logo_url, org_type, business_nature, country, fy_start_month, org_config';
                const membersOf = (cols: string) => supabase
                    .from('org_members')
                    .select(`role, org:orgs!org_members_org_id_fkey(${cols})`)
                    .eq('user_id', profile.id);

                // Undefined column (42703) is the pre-apply case — the founder applies
                // migrations by hand, so this client ships before `is_practice` exists
                // and PostgREST rejects the whole select when a column is missing.
                // Retry without it; but if the retry fails too, that is a read failure
                // and NOT an empty membership list.
                //
                // orgclient: and WHICH of those orgs are on our office's client list.
                // Asked alongside the memberships rather than after them, because it
                // decides how the switcher GROUPS the very list this query returns
                // and a second round trip would show the wrong grouping first. RLS
                // on `practice_clients` scopes the read to offices this user belongs
                // to, so anyone without a practice gets an empty list, not an error.
                //
                // orgidfast: and every GSTIN each business holds, embedded in the
                // same request (its GST registrations and its places' own GSTINs),
                // so the switcher's G¹ G² G³ never cost a call or a wait on click.
                // Each table has one key to `orgs`, so the embed is unambiguous.
                // Should it ever be refused, the read falls back to what it asked
                // before this, and the chips show the GSTIN in Settings alone. A
                // network failure skips the fallbacks: the retry below handles it.
                // regnos: and a non-profit's s.332 / s.354 / FCRA / DARPAN numbers
                // (npo_approvals, one key to orgs), only columns its first migration made.
                const GSTIN_EMBED = 'org_registrations(identifier, kind, status), branches(gstin, is_active), '
                    + 'npo_approvals(kind, approval_number, status, stage, order_date, covers_to_fy), '
                    // and the directors register with the board's named ROC signatory, for
                    // the DIN of the director who signs (signerDin.ts).
                    + 'directors(name, din, pan, designation, date_of_appointment, date_of_cessation), '
                    + 'statutory_roles(role_key, holder_pan, holder_name, vacated_on)';
                const [withAll, clientLinks] = await Promise.all([
                    membersOf(`${ORG_COLS}, is_practice, ${GSTIN_EMBED}`),
                    supabase.from('practice_clients').select('client_org_id')
                ]);
                const withoutGstins = async () => {
                    reportEvent('org_gstins_unreadable', { code: withAll.error?.code ?? 'none' });
                    const withFlag = await membersOf(`${ORG_COLS}, is_practice`);
                    return withFlag.error ? membersOf(ORG_COLS) : withFlag;
                };
                // A database that is out of reach is retried whole, not narrowed column by column.
                const res = withAll.error && !isNetworkFailure(withAll.error) && !isDatabaseWaking(withAll.error) ? await withoutGstins() : withAll;
                if (res.error) {
                    if (await transientRetryWait(res.error, attempt)) continue;
                    const dead = await get().endDeadSession(res.error, 'memberships', !revived);
                    if (dead === 'revived') { revived = true; continue; }
                    if (dead) return;
                    console.error('Error fetching memberships:', res.error);
                    reportEvent('orgs_unreadable', { at: 'memberships', code: isNetworkFailure(res.error) ? 'network' : (res.error.code ?? 'none') });
                    set({ loading: false, initialized: true, orgsUnreadable: dbErrorMessage(res.error) });
                    return;
                }

                // A failed client-link read costs nobody their org list. The old
                // grouping is the worst case, so an error reads as "no clients"
                // rather than joining `orgsUnreadable` and blanking the switcher.
                if (clientLinks.error) console.warn('practice_clients unreadable, grouping by role only:', clientLinks.error);
                const clientOrgIds = new Set(
                    (clientLinks.data ?? [])
                        .map((r: { client_org_id: string | null }) => r.client_org_id)
                        .filter((id): id is string => !!id)
                );

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const orgs: Org[] = (res.data ?? []).map((m: any) => {
                    // The embedded rows become `gstins` and are not kept on the org.
                    const {
                        org_registrations: regs, branches: places, npo_approvals: npo,
                        directors, statutory_roles: roles, ...org
                    } = m.org ?? {};
                    return {
                        ...org,
                        role: m.role,
                        // An office is never its own client; a row saying so is bad data,
                        // and honouring it would hide the firm from its own owner.
                        is_practice_client: !m.org?.is_practice && clientOrgIds.has(m.org?.id),
                        gstins: gstinsOf(regs, places),
                        npo_numbers: npoNumbersOf(npo),
                        signer_din: signerDinOf(directors, roles),
                    };
                });

                // Restore the last active org if the user still belongs to it,
                // otherwise fall back to the first org.
                const savedOrgId = recallActiveOrg();
                const restored = (savedOrgId && orgs.find(o => o.id === savedOrgId)) || orgs[0] || null;

                // Rehydrate "viewing client" context (banner + Exit button) across
                // reloads: valid only if the saved practice org is still ours and
                // the restored org is a different (client) org.
                const vc = recallViewingClient();
                const practiceOrg = vc && orgs.find(o => o.id === vc.practiceOrgId);
                const stillViewing = !!(vc && practiceOrg && restored && restored.id !== vc.practiceOrgId);
                if (vc && !stillViewing) rememberViewingClient(null);

                set({
                    profile: profile as PublicUser | null,
                    orgs,
                    orgsUnreadable: null,
                    currentOrg: restored,
                    originalPracticeOrg: stillViewing ? practiceOrg : null,
                    viewingAsClient: stillViewing,
                    viewingClientName: stillViewing ? (vc!.clientName || restored!.name) : null,
                    loading: false,
                    initialized: true
                });

                // Tie funnel/error events to this user (no-op unless a telemetry
                // provider is configured). Uses the public.users id as distinct id.
                if (profile?.id) identify(profile.id, { email: user.email });
                return;

            } catch (error) {
                if (await transientRetryWait(error, attempt)) continue;
                console.error('Error refreshing profile:', error);
                const dead = await get().endDeadSession(error, 'refreshProfile', !revived);
                if (dead === 'revived') { revived = true; continue; }
                if (dead) return;
                reportEvent('orgs_unreadable', { at: 'refreshProfile', code: isNetworkFailure(error) ? 'network' : 'threw' });
                set({ loading: false, initialized: true, orgsUnreadable: dbErrorMessage(error) });
                return;
            }
        }
    },

    signInWithGoogle: async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin
            }
        });
        return { error };
    },

    signInWithEmail: async (email: string) => {
        // Passwordless email as a fallback to Google (some users have no Google
        // account). shouldCreateUser defaults to true, so this both signs in
        // existing users and creates new ones — and because the account isn't
        // usable until the emailed link is clicked, magic-link signup is
        // self-confirming and resists spam without blocking real users.
        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                emailRedirectTo: window.location.origin,
            }
        });
        // Remembered even when the send failed: the address is still the one they
        // are trying to sign in with, and the retry should not make them type it
        // again. See shared/auth/lastEmail.ts for why this outlives the tab.
        rememberAuthEmail(email);
        return { error };
    },

    signInWithEmailCode: async (email: string, code: string) => {
        // type 'email' covers both an existing user signing in and a brand-new
        // one confirming — GoTrue picks; 'magiclink' would reject the second.
        const { error } = await supabase.auth.verifyOtp({
            email,
            token: code.replace(/\D/g, ''),
            type: 'email',
        });
        return { error };
    },

    signInWithPassword: async (email: string, password: string) => {
        // The everyday path once a password exists. Sign-UP deliberately has no
        // password field: the link/Google proof of a real mailbox is the spam
        // gate, and a password invented at signup would prove nothing. See
        // shared/auth/passwordPrompt.ts.
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error };
    },

    setPassword: async (password: string) => {
        // `has_password` is ours, not Supabase's — there is no client-visible flag
        // for "this account can sign in with a password", and `identities` says
        // `provider: 'email'` for a magic-link account too. Without our own flag
        // the set-password screen would ask forever.
        const { error } = await supabase.auth.updateUser({
            password,
            data: { has_password: true },
        });
        if (!error) {
            const { data } = await supabase.auth.getUser();
            if (data.user) set({ user: data.user });
        }
        return { error };
    },

    dismissPasswordPrompt: async () => {
        const { error } = await supabase.auth.updateUser({ data: { password_prompt_dismissed: true } });
        if (!error) {
            const { data } = await supabase.auth.getUser();
            if (data.user) set({ user: data.user });
        }
    },

    sendPasswordReset: async (email: string) => {
        // ⚠ `/auth/reset` must be in Supabase → Authentication → URL Configuration →
        // Redirect URLs. If it is not, GoTrue silently substitutes Site URL and the
        // person lands on the marketing page — the exact silent failure linkError.ts
        // exists for.
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/auth/reset`,
        });
        return { error };
    },

    signOut: async () => {
        // staylogged: THIS device only. supabase-js defaults to scope 'global',
        // which revokes every refresh token the person holds, so signing out of
        // the office laptop silently signed their phone out too, and so did
        // any device whose session ended on its own (endDeadSession).
        // webpush: and a phone signed out stops getting this person's alerts.
        // First, while the session can still remove its own row.
        await forgetPushOnSignOut();
        await supabase.auth.signOut({ scope: 'local' });
        resetIdentity(); // stop attributing events to the signed-out user
        forgetAuthEmail(); // a shared machine must not name the last person to sign in
        forgetDayBook(); // nor hold the last practice's client list in memory
        forgetPages(); // nor its CRM, team, requests or partner ledger
        forgetRecentPlaces(); // nor the places this person opened from the search box (whereis-6)
        forgetAskAiMemory(); // nor what Ask AI answered in search (whereis-8)
        set({ session: null, user: null, profile: null, orgs: [], orgsUnreadable: null, currentOrg: null, viewingAsClient: false, viewingClientName: null, originalPracticeOrg: null });
        rememberViewingClient(null);
    },

    switchOrg: (orgId: string) => {
        const org = get().orgs.find(o => o.id === orgId);
        if (org) {
            // A deliberate org switch ends any "viewing client" visit — the
            // banner/Exit context would otherwise go stale (and persist).
            set({ currentOrg: org, viewingAsClient: false, viewingClientName: null, originalPracticeOrg: null });
            rememberActiveOrg(org.id);
            rememberViewingClient(null);
        }
    },

    updateOrgLocal: (orgId, patch) => {
        const { orgs, currentOrg } = get();
        set({
            orgs: orgs.map(o => (o.id === orgId ? { ...o, ...patch } : o)),
            currentOrg: currentOrg?.id === orgId ? { ...currentOrg, ...patch } : currentOrg,
        });
    },

    /**
     * Enter a client's org — switches currentOrg to the client org,
     * saves original practice org for restoration.
     * This is the core "Enter Client" flow for CAs.
     */
    enterClientOrg: (clientOrgId: string, clientName: string) => {
        const { currentOrg, orgs, viewingAsClient } = get();
        // Don't nest entries — if already viewing, exit first
        if (viewingAsClient) {
            get().exitClientOrg();
        }
        const clientOrg = orgs.find(o => o.id === clientOrgId);
        if (clientOrg && currentOrg) {
            set({
                originalPracticeOrg: currentOrg,
                currentOrg: clientOrg,
                viewingAsClient: true,
                viewingClientName: clientName,
            });
            rememberActiveOrg(clientOrg.id);
            rememberViewingClient({ practiceOrgId: currentOrg.id, clientName });
        }
    },

    /**
     * Exit back to the CA's practice org.
     */
    exitClientOrg: () => {
        const { originalPracticeOrg } = get();
        if (originalPracticeOrg) {
            set({
                currentOrg: originalPracticeOrg,
                originalPracticeOrg: null,
                viewingAsClient: false,
                viewingClientName: null,
            });
            rememberActiveOrg(originalPracticeOrg.id);
        }
        rememberViewingClient(null);
    },
}));
